import { BLACK_TEXT, CORAL_600, GRAY_700, GRAY_900, InterVariableFontFamily } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { FC, useState } from 'react'
import { PLAN_COPY_KEY, PLAN_FEATURES, PLAN_ICONS, getEverythingPrefixKey } from 'constants/subscription'
import { PlanFeatureList, PlanIconCircle } from 'components/common/ChoosePlan'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { ChargebeeModal } from 'components/common/ChargebeeModal'
import { MUIButton } from 'components/common/MUIButton'
import { MUITooltip } from 'components/common/MUITooltip'
import { ManageSubscriptionModal } from './ManageSubscriptionModal.component'
import { SubscriptionPeriod, SubscriptionPlanType, SubscriptionStatus } from 'models/user/userWorkspaceDTOs'
import moment from 'moment-timezone'
import { useBillingPortal } from 'utils/hooks/useBillingPortal'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

interface Props {
  /** The workspace's active subscription plan */
  planType: SubscriptionPlanType
  /** Callback when a plan card is selected in the manage subscription modal */
  onSelectPlan: (plan: SubscriptionPlanType, period: SubscriptionPeriod) => void
}

export const PlanSummaryCard: FC<Props> = ({ planType, onSelectPlan }) => {
  const { t } = useTranslation(['workspace_settings', 'pricing_page'])
  const { currentUserWorkspace, currentUserWorkspaceMembership, userWorkspacesQuery } = useUserData()
  const [isManageModalOpen, setIsManageModalOpen] = useState(false)

  // Falls back to the selected workspace's own status (/user/me): the membership object comes
  // from a separate query that can fail, and an absent status must not read as "not in grace
  // period" — that would silently open the gate exactly when it has to stay shut.
  const workspaceStatus = currentUserWorkspaceMembership?.workspaceStatus ?? currentUserWorkspace?.status
  const isInGracePeriod = workspaceStatus === SubscriptionStatus.IN_GRACE_PERIOD
  const isWorkspaceStatusUnknown = !workspaceStatus
  // Don't trust the cached IN_GRACE_PERIOD value while membership data is being refetched
  // (e.g. right after a workspace switch) — wait for fresh data before enabling the billing portal.
  const isMembershipFetching = userWorkspacesQuery.isFetching
  const isBillingPortalBlocked = isMembershipFetching || isWorkspaceStatusUnknown || isInGracePeriod

  const {
    isFetchingPortalLink,
    isBillingPortalOpen,
    hostedPageUrl,
    openBillingPortal,
    closeBillingPortal,
    onBillingPortalOpened,
  } = useBillingPortal(currentUserWorkspace?.id || '')

  const Icon = PLAN_ICONS[planType]
  const features = PLAN_FEATURES[planType]
  const copyKey = PLAN_COPY_KEY[planType]
  const planName = t(`pricing_page:plans.${copyKey}.name`)
  const tagline = t(`pricing_page:plans.${copyKey}.tagline`)
  // Paid plans list only what they add on top of the plan below them, so without this line
  // Enterprise reads as three features in total — fewer than the free Starter card advertises.
  const everythingPrefixKey = getEverythingPrefixKey(planType)

  return (
    <>
      <BorderBoxWrapper padding={2.4}>
        <Stack direction="row" alignItems="center" gap="1.6rem" mb="2.4rem">
          <PlanIconCircle size="md">
            <Icon />
          </PlanIconCircle>

          <Stack gap="0.4rem" flex={1}>
            <Typography variant="display-xs" fontWeight={500} color={GRAY_900} fontFamily={InterVariableFontFamily}>
              {t('workspace_settings:plan.workspace_on_plan', { plan: planName })}
            </Typography>
            <Typography variant="text-md" fontWeight={350} color={GRAY_700}>
              {tagline}
            </Typography>
            {!!currentUserWorkspaceMembership?.terminatedAt && (
              <Typography variant="text-sm" fontWeight={400} color={CORAL_600}>
                {t('workspace_settings:workspace_active_until', { date: moment(currentUserWorkspaceMembership.terminatedAt).format('DD.MM.YYYY') })}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" gap="0.8rem" flexShrink={0}>

            <MUITooltip
              content={!isMembershipFetching && isInGracePeriod ? t('workspace_settings:plan.billing_information_grace_period_tooltip') : undefined}
              placement="top"
              sx={{ width: '26rem' }}
            >
              <MUIButton
                type="secondaryNoBorder"
                disabled={isBillingPortalBlocked || isFetchingPortalLink}
                // Portal call only — the workspace-list query refetches for unrelated reasons
                isLoading={isFetchingPortalLink}
                onClick={openBillingPortal}
              >
                {t('workspace_settings:plan.billing_information')}
              </MUIButton>
            </MUITooltip>

            <MUIButton type="secondaryBorder" onClick={() => setIsManageModalOpen(true)}>
              {t('workspace_settings:plan.manage_subscription')}
            </MUIButton>

          </Stack>
        </Stack>

        {everythingPrefixKey && (
          <Typography variant="text-md" fontWeight={450} color={BLACK_TEXT} sx={{ display: 'block', mb: '2rem' }}>
            {t(everythingPrefixKey)}
          </Typography>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1.2rem',
          }}
        >
          <PlanFeatureList features={features} size="md" />
        </Box>
      </BorderBoxWrapper>

      <ManageSubscriptionModal
        isOpen={isManageModalOpen}
        onClose={() => setIsManageModalOpen(false)}
        currentPlan={planType}
        onSelectPlan={onSelectPlan}
        terminatedAt={currentUserWorkspaceMembership?.terminatedAt}
      />

      <ChargebeeModal
        title={t('workspace_settings:plan.billing_information')}
        hostedPageUrl={hostedPageUrl}
        isOpen={isBillingPortalOpen}
        handleClose={closeBillingPortal}
        onIframeLoaded={onBillingPortalOpened}
      />
    </>
  )
}
